import { roles } from "core/constants";
const { VAMPIRE, GARLIC_FARMER, PRIEST, CULLEN, MAYOR, LIBRARIAN, HUMAN } =
  roles;

export const getIsVamp = (role) => role === VAMPIRE;
export const getIsCullen = (role) => role === CULLEN;
export const getIsUndead = (role) => getIsCullen(role) || getIsVamp(role);
export const getIsGarlicFarmer = (role) => role === GARLIC_FARMER;
export const getIsPriest = (role) => role === PRIEST;
export const getIsMayor = (role) => role === MAYOR;
export const getIsLibrarian = (role) => role === LIBRARIAN;
export const getIsHuman = (role) => role === HUMAN;

// Seeded random number generator for deterministic randomization
const seededRandom = (seed) => {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
};

// Calculate which roles will be assigned for a given player count and role configuration
// Mirrors backend logic in selectRandomRolesForPlayerCount
export const calculateRoleBreakdown = (
  roleConfig,
  playerCount,
  seed = null
) => {
  if (!roleConfig) {
    return null;
  }

  const {
    vampires = 0,
    garlicFarmers = 0,
    priests = 0,
    librarians = 0,
    mayors = 0,
    cullens = 0,
  } = roleConfig;

  // Calculate total configured roles
  const totalConfigured =
    vampires + garlicFarmers + priests + librarians + mayors + cullens;

  // If total configured is less than or equal to player count, use all roles
  if (totalConfigured <= playerCount) {
    const humans = playerCount - totalConfigured;
    return {
      totalConfigured,
      playerCount,
      willBeAssigned: {
        vampires,
        garlicFarmers,
        priests,
        librarians,
        mayors,
        cullens,
        humans,
      },
      willBeSkipped: {},
      needsRandomization: false,
    };
  }

  // More roles configured than players - need to randomly select
  // Always include all vampires first (they have priority)
  const result = {
    vampires: Math.min(vampires, playerCount),
    garlicFarmers: 0,
    priests: 0,
    librarians: 0,
    mayors: 0,
    cullens: 0,
  };

  let slotsRemaining = playerCount - result.vampires;

  // Build pool of optional roles (non-vampire roles)
  const optionalRoles = [];
  if (garlicFarmers) {
    for (let i = 0; i < garlicFarmers; i++) {
      optionalRoles.push("garlicFarmers");
    }
  }
  if (priests) {
    for (let i = 0; i < priests; i++) {
      optionalRoles.push("priests");
    }
  }
  if (librarians) {
    for (let i = 0; i < librarians; i++) {
      optionalRoles.push("librarians");
    }
  }
  if (mayors) {
    for (let i = 0; i < mayors; i++) {
      optionalRoles.push("mayors");
    }
  }
  if (cullens) {
    for (let i = 0; i < cullens; i++) {
      optionalRoles.push("cullens");
    }
  }

  // Shuffle optional roles using seed if provided
  const random = seed !== null ? seededRandom(seed) : Math.random;
  const shuffledRoles = [...optionalRoles];
  for (let i = shuffledRoles.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffledRoles[i], shuffledRoles[j]] = [shuffledRoles[j], shuffledRoles[i]];
  }

  // Select roles from shuffled pool until we fill all slots
  for (let i = 0; i < Math.min(slotsRemaining, shuffledRoles.length); i++) {
    result[shuffledRoles[i]]++;
  }

  // Calculate what was skipped
  const willBeSkipped = {
    garlicFarmers: garlicFarmers - result.garlicFarmers,
    priests: priests - result.priests,
    librarians: librarians - result.librarians,
    mayors: mayors - result.mayors,
    cullens: cullens - result.cullens,
  };

  // Calculate humans (any remaining slots)
  const assignedSpecialRoles =
    result.vampires +
    result.garlicFarmers +
    result.priests +
    result.librarians +
    result.mayors +
    result.cullens;
  const humans = playerCount - assignedSpecialRoles;

  return {
    totalConfigured,
    playerCount,
    willBeAssigned: {
      ...result,
      humans,
    },
    willBeSkipped,
    needsRandomization: true,
  };
};

// Format role breakdown into human-readable text
export const formatRoleWarning = (breakdown) => {
  if (!breakdown) return "";

  const { willBeAssigned, willBeSkipped, playerCount, needsRandomization } =
    breakdown;

  // Role name mapping for display
  const roleNames = {
    vampires: "Vampire",
    garlicFarmers: "Garlic Farmer",
    priests: "Priest",
    librarians: "Librarian",
    mayors: "Mayor",
    cullens: "Cullen",
    humans: "Human",
  };

  // Format assigned roles
  const assignedParts = [];
  Object.entries(willBeAssigned).forEach(([role, count]) => {
    if (count > 0) {
      const name = roleNames[role] || role;
      const plural = count > 1 ? "s" : "";
      assignedParts.push(`${count} ${name}${plural}`);
    }
  });

  let message = `more roles than players: ${assignedParts.join(", ")}`;

  // Add skipped roles if any
  if (needsRandomization) {
    const skippedParts = [];
    Object.entries(willBeSkipped).forEach(([role, count]) => {
      if (count > 0) {
        const name = roleNames[role] || role;
        const plural = count > 1 ? "s" : "";
        skippedParts.push(`${count} ${name}${plural}`);
      }
    });
  }

  return message;
};

// Format role breakdown as a list for modal display
export const formatRoleBreakdownAsList = (willBeAssigned) => {
  const roleNames = {
    vampires: "Vampire",
    garlicFarmers: "Garlic Farmer",
    priests: "Priest",
    librarians: "Librarian",
    mayors: "Mayor",
    cullens: "Cullen",
    humans: "Human",
  };

  return Object.entries(willBeAssigned)
    .filter(([_, count]) => count > 0)
    .map(([role, count]) => {
      const name = roleNames[role] || role;
      const plural = count > 1 ? "s" : "";
      return `${count} ${name}${plural}`;
    });
};
